import { leadName } from "@/lib/campaign";
import { stageText } from "@/lib/contacts";
import type { LeadCard, LeadSibling } from "@/lib/types";

/**
 * A person's other open cases (ACL-326 step 6, ADR-223 in the platform repo): the records with the
 * card's phone that the people table says are the same person's, still open (not in an exit stage)
 * and the reader's to open. What the call's outcome window offers as "This call was about". Pure.
 */
export function openCasesOf(card: Pick<LeadCard, "siblings"> | undefined): LeadSibling[] {
  return (card?.siblings ?? []).filter(
    (s) => s.same_person === true && s.open === true && s.may_open === true && !s.deleted,
  );
}

/**
 * Every open case on the card's number, any person (ACL-671, ADR-226 in the platform repo): a shared
 * phone is answered by whoever is there, so the call may be filed under the daughter on her mother's
 * number. The reader's to open, not deleted. What the outcome window offers as "This call was about".
 */
export function casesOnTheNumber(card: Pick<LeadCard, "siblings"> | undefined): LeadSibling[] {
  return (card?.siblings ?? []).filter((s) => s.open === true && s.may_open === true && !s.deleted);
}

/** The option's words: the same person's case by its accident and stage; somebody else's by their
 *  name first, so the rep sees who. */
export function caseOptionLabel(sibling: Pick<LeadSibling, "accident_number" | "stage" | "same_person" | "first_name" | "last_name">): string {
  const label = caseLabel(sibling);
  if (sibling.same_person === true) return label;
  return `${leadName({ first_name: sibling.first_name ?? null, last_name: sibling.last_name ?? null }) ?? "Someone else"} · on this number · ${label}`;
}

/** "A-000002 · New", or the stage alone for a record in no accident yet. */
export function caseLabel(sibling: Pick<LeadSibling, "accident_number" | "stage">): string {
  const stage = stageText(sibling.stage).label;
  return sibling.accident_number ? `${sibling.accident_number} · ${stage}` : stage;
}
